import React, { useEffect, useState } from 'react';
import { Pie } from 'react-chartjs-2';
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';
import { StatusBarChart } from './StatusBarChart';
import { useDispatch } from 'react-redux';
import { getDashboardData } from 'src/store/slices/callSlice';
import { ThunkDispatch } from '@reduxjs/toolkit';
import { composeThunkAction } from 'src/common/utils';
import { CALL, DASBOARDSUMMARY, FULFILLED } from 'src/common/constants';

ChartJS.register(ArcElement, Tooltip, Legend);

interface PieChartData {
  name: string;
  value: number;
  percentage: number;
}

interface BarChartData {
  name: string;
  value: number;
}

export const DashboardPieChart: React.FC = () => {
  const [pieData, setPieData] = useState<PieChartData[]>([]);
  const [barData, setBarData] = useState<BarChartData[]>([]);
  const dispatch: ThunkDispatch<object, object, any> = useDispatch();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res: any = await dispatch(getDashboardData())
        if (res.type === `${composeThunkAction(CALL, DASBOARDSUMMARY)}/${FULFILLED}`) {
          const data = res.payload.data.data;
          const total = data.booked + data.transferred + data.answered + data.askedTransfer;
          const pieChartFormattedData = [
            { name: 'BOOKED', value: data.booked, percentage: total > 0 ? Math.round((data.booked / total) * 100) : 0 },
            { name: 'TRANSFERRED', value: data.transferred, percentage: total > 0 ? Math.round((data.transferred / total) * 100) : 0 },
            { name: 'ANSWERED', value: data.answered, percentage: total > 0 ? Math.round((data.answered / total) * 100) : 0 },
            { name: 'Asked Transfer', value: data.askedTransfer, percentage: total > 0 ? Math.round((data.askedTransfer / total) * 100) : 0 },
            { name: 'ANSWERED SHORT CALL', value: data.answeredShortCall, percentage: total > 0 ? Math.round((data.answeredShortCall / total) * 100) : 0 },
          ];

          const barChartFormattedData: any = Object.entries(data.labels).map(([key, value]) => ({
            name: key,
            value,
          }));

          setPieData(pieChartFormattedData);
          setBarData(barChartFormattedData);
        }
      } catch (error) {
        console.error('Error fetching data', error);
      }
    };

    fetchData();
  }, []);

  const pieChartData = {
    labels: pieData.map(item => item.name),
    datasets: [
      {
        data: pieData.map(item => item.value),
        backgroundColor: ['#2394d2', '#FF8042', '#00C49F', '#00BFFF', '#FFA07A'],
        borderColor: '#f2f2f2',
        borderWidth: 2,
      },
    ],
  };

  const pieChartOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: 'bottom' as const,
        labels: {
          color: 'white',
          generateLabels: function (chart: any) {
            const dataset = chart.data.datasets[0];
            return chart.data.labels.map((label: string, i: number) => ({
              text: `${label}: ${dataset.data[i]}`,
              fillStyle: dataset.backgroundColor[i],
              strokeStyle: dataset.borderColor,
              lineWidth: dataset.borderWidth,
              hidden: isNaN(dataset.data[i]) || dataset.data[i] === null,
              index: i,
            }));
          },
          boxWidth: 20,
          boxHeight: 12,
          padding: 10,
        },
      },
      tooltip: {
        callbacks: {
          label: function (context: any) {
            const label = context.label || '';
            const value = context.raw || 0;
            const percentage = pieData.find(p => p.name === label)?.percentage || 0;
            return `${label}: ${value} (${percentage}%)`;
          },
        },
      },
    },
  };

  return (
    <div>
      {/* <h2 className="h4 fw-semibold mb-4">Booking Report</h2> */}
      <div className='row'>
        <div className='col-lg-8 mb-4'>
          <div className='card graph-card'>
            <div className='card-body'>
              <div className='d-flex flex-column gap-3 h-100'>
                <div className='chart-label'>Last 24 hour Status Reason Data</div>
                <div className='chart-graph'>
                  <StatusBarChart data={barData} />
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className='col-lg-4 mb-4'>
          <div className='card graph-card'>
            <div className='card-body'>
              <div className='d-flex flex-column gap-3 h-100'>
                <div className='chart-label'>Last 24 hour Bookings</div>
                <div className='chart-graph flex-grow-1 d-flex justify-content-center align-items-center'>
                  {pieData && pieData.some(d => d.value > 0) ? <Pie data={pieChartData} options={pieChartOptions} /> : <span className='text-muted'>No Data Found</span>}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};